/**
 * The Inspector tab (1.J deliverables 17 and 18), a `rail.tabs` registration of order 60: this
 * page's links, and for the selected link its type, ends, home control and editable fields.
 * Every write goes to the document that owns the link (its source), with the link's `baseRv`.
 */
import { ApiError, type LinkTypeV1, type LinkV1, type SlotComponentProps } from '@codex/contracts';
import { Button, Checkbox, Skeleton, SrdBadge, TextField, TypeGlyph } from '@codex/ui';
import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, type RefObject, useEffect, useId, useMemo, useRef, useState } from 'react';
import { docHref } from '../../editor-services/adapter.js';
import { useNotify } from '../../shell/notices.js';
import { useOpenDoc } from '../../shell/open-doc.js';
import { useLinksApi } from '../api.js';
import { useLinkFlags, useLinkWhen } from '../chrono.js';
import { reportFailure } from '../failure.js';
import { useLinksLive, usePageSeen } from '../live.js';
import { DUPLICATE_NOTICE, removedNotice } from '../messages.js';
import { DocPicker } from '../picker/doc-picker.js';
import { useLinkList, useLinkTypes, useTitles } from '../queries.js';
import { pruneSelection, selectLink, useSelectedLink } from '../selection.js';
import { glyphFor } from '../story/glyph.js';
import { ValidityCells, type WhenFacts } from '../validity/validity-cells.js';
import { hasValidity } from '../validity/validity-model.js';
import {
  changedPatch,
  groupLinks,
  homeControl,
  type MetaField,
  type MetaInput,
  metaFields,
  metaInputs,
  offeredTypes,
} from './inspector-model.js';
import { WhenItHolds } from './when-it-holds.js';
import '../links.css';

export const NO_LINKS_TEXT = 'This page has no links yet.';
export const VALUE_REFUSED_TEXT = "That value isn't allowed for this link.";
export const INSPECTOR_FAILED_TEXT = "Links couldn't be loaded.";

const labelOfKey = (key: string) =>
  key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());

/** Where focus goes when the tab swaps its overview and editor (a button unmounts under it). */
type FocusIntent = { kind: 'editor' } | { kind: 'row'; id: string } | { kind: 'heading' } | null;

export function InspectorTab({ docId }: SlotComponentProps) {
  const intent = useRef<FocusIntent>(null);
  const list = useLinkList(docId);
  const types = useLinkTypes();
  const selectedId = useSelectedLink(docId);
  const seen = usePageSeen(docId);
  const flags = useLinkFlags();
  const when = useLinkWhen(flags.chrono);
  const facts: WhenFacts = { calendar: when.calendar, now: when.now };
  const links = list.data;
  const ends = useMemo(
    () => (links ?? []).map((l) => (l.sourceId === docId ? l.targetId : l.sourceId)),
    [links, docId],
  );
  const titles = useTitles(ends);
  useLinksLive(docId, ends, seen, {
    validity: flags.chrono && (links?.some(hasValidity) ?? false),
  });

  useEffect(() => {
    if (links) pruneSelection(docId, new Set(links.map((l) => l.id)));
  }, [links, docId]);

  const titleOf = useMemo(() => {
    const map = new Map((titles.data ?? []).map((t) => [t.id, t] as const));
    return (id: string) => map.get(id)?.title ?? '';
  }, [titles.data]);

  let body: ReactNode;
  if (list.isError) {
    body = (
      <div>
        <p role="alert">{INSPECTOR_FAILED_TEXT}</p>
        <Button size="sm" onClick={() => void list.refetch()}>
          Try again
        </Button>
      </div>
    );
  } else if (!links) {
    body = <Skeleton lines={3} label="Loading links" />;
  } else {
    const selected = links.find((l) => l.id === selectedId);
    const otherId =
      selected && (selected.sourceId === docId ? selected.targetId : selected.sourceId);
    body = selected ? (
      <>
        <LinkEditor
          key={`${selected.id}:${selected.rv}`}
          docId={docId}
          link={selected}
          type={types.data?.find((t) => t.id === selected.linkTypeId)}
          titleOf={titleOf}
          isReference={(id) => titles.data?.find((t) => t.id === id)?.library === 'reference'}
          intent={intent}
        />
        {flags.chrono && otherId && (
          <WhenItHolds
            key={selected.id}
            docId={docId}
            link={selected}
            otherId={otherId}
            otherTitle={titleOf(otherId)}
            otherTypeKey={titles.data?.find((t) => t.id === otherId)?.typeKey ?? null}
            facts={facts}
          />
        )}
      </>
    ) : (
      <LinkOverview
        docId={docId}
        intent={intent}
        links={links}
        types={types.data ?? []}
        titleOf={titleOf}
        typeKeyOf={(id) => titles.data?.find((t) => t.id === id)?.typeKey ?? 'note'}
        isReference={(id) => titles.data?.find((t) => t.id === id)?.library === 'reference'}
        facts={flags.chrono ? facts : null}
      />
    );
  }

  return (
    <section
      data-testid="links.inspector.root"
      aria-label="Inspector"
      className="cx-links-tab"
      data-selected-link={selectedId ?? undefined}
    >
      {body}
    </section>
  );
}

function LinkOverview({
  docId,
  links,
  types,
  titleOf,
  typeKeyOf,
  isReference,
  facts,
  intent,
}: {
  intent: RefObject<FocusIntent>;
  docId: string;
  links: readonly LinkV1[];
  types: readonly LinkTypeV1[];
  titleOf: (id: string) => string;
  typeKeyOf: (id: string) => string;
  isReference: (id: string) => boolean;
  facts: WhenFacts | null;
}) {
  const groups = groupLinks(links, types, docId);
  const [adding, setAdding] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  // Back from the editor, or a removed link: put focus where the keyboard user was.
  useEffect(() => {
    const wanted = intent.current;
    intent.current = null;
    if (!wanted || wanted.kind === 'editor') return;
    const row =
      wanted.kind === 'row'
        ? root.current?.querySelector<HTMLElement>(`[data-link-id="${wanted.id}"]`)
        : null;
    (row ?? heading.current)?.focus();
  }, [intent]);
  return (
    <div ref={root}>
      <div className="cx-links-story__head">
        <h3 ref={heading} tabIndex={-1} className="cx-links-tab__heading">
          Links
        </h3>
        <AddLink docId={docId} types={types} open={adding} onOpen={setAdding} />
      </div>
      {groups.length === 0 ? (
        <p className="cx-links-story__empty">{NO_LINKS_TEXT}</p>
      ) : (
        groups.map((group, index) => (
          <section key={group.label} aria-label={group.label}>
            <h4 id={`insp-grp-${docId}-${index}`} className="cx-links-tab__group">
              {group.label}
            </h4>
            <ul className="cx-links-tab__list" aria-labelledby={`insp-grp-${docId}-${index}`}>
              {group.items.map((item) => (
                <li key={item.link.id}>
                  <button
                    type="button"
                    className="cx-links-tab__select"
                    data-link-id={item.link.id}
                    onClick={() => {
                      intent.current = { kind: 'editor' };
                      selectLink(docId, item.link.id);
                    }}
                  >
                    <TypeGlyph type={glyphFor(typeKeyOf(item.otherId))} />
                    <span>{titleOf(item.otherId)}</span>
                    {isReference(item.otherId) && <SrdBadge />}
                    {item.link.label && (
                      <span className="cx-links-row__beat">{item.link.label}</span>
                    )}
                    {facts && <ValidityCells link={item.link} docId={docId} facts={facts} />}
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </div>
  );
}

function AddLink({
  docId,
  types,
  open,
  onOpen,
}: {
  docId: string;
  types: readonly LinkTypeV1[];
  open: boolean;
  onOpen: (open: boolean) => void;
}) {
  const api = useLinksApi();
  const notify = useNotify();
  const client = useQueryClient();
  const selectId = useId();
  const offered = useMemo(() => offeredTypes(types), [types]);
  const [typeKey, setTypeKey] = useState('');
  const [button, setButton] = useState<HTMLButtonElement | null>(null);
  const chosen = typeKey || offered[0]?.key || '';

  return (
    <>
      <Button ref={setButton} size="sm" aria-expanded={open} onClick={() => onOpen(!open)}>
        Add link
      </Button>
      {open && (
        <fieldset className="cx-links-add">
          <legend className="cx-visually-hidden">Add link</legend>
          <label htmlFor={selectId} className="cx-field__label">
            Link type
          </label>
          <select
            id={selectId}
            className="cx-links-select"
            value={chosen}
            onChange={(event) => setTypeKey(event.target.value)}
          >
            {offered.map((t) => (
              <option key={t.id} value={t.key}>
                {t.label}
              </option>
            ))}
          </select>
          <DocPicker
            label="Link to a page"
            selfId={docId}
            createType={null}
            trigger={button}
            onClose={() => onOpen(false)}
            onCreate={() => undefined}
            onPick={(hit) => {
              onOpen(false);
              void (async () => {
                try {
                  const result = await api.create(docId, {
                    linkTypeKey: chosen,
                    targetId: hit.id,
                    isPrimary: false,
                  });
                  if (result.warnings.some((w) => w.kind === 'duplicate')) notify(DUPLICATE_NOTICE);
                  await Promise.all([
                    client.invalidateQueries({ queryKey: ['links'] }),
                    client.invalidateQueries({ queryKey: ['tree'] }),
                  ]);
                } catch (error) {
                  reportFailure(notify, error);
                }
              })();
            }}
          />
        </fieldset>
      )}
    </>
  );
}

function LinkEditor({
  docId,
  link,
  type,
  titleOf,
  isReference,
  intent,
}: {
  intent: RefObject<FocusIntent>;
  docId: string;
  link: LinkV1;
  type: LinkTypeV1 | undefined;
  titleOf: (id: string) => string;
  isReference: (id: string) => boolean;
}) {
  const api = useLinksApi();
  const notify = useNotify();
  const client = useQueryClient();
  const open = useOpenDoc();
  const noteId = useId();
  const fields = useMemo(() => (type ? metaFields(type.metaSchema) : null), [type]);
  const [typed, setTyped] = useState({
    label: link.label ?? '',
    role: link.role ?? '',
    note: link.note ?? '',
  });
  const [inputs, setInputs] = useState<Record<string, MetaInput>>(() =>
    metaInputs(fields ?? [], link.meta),
  );
  // The meta keys the refused save changed; empty when the refusal names no field.
  const [refused, setRefused] = useState<readonly string[] | null>(null);
  const form = useRef<HTMLFormElement>(null);
  useEffect(() => {
    if (intent.current?.kind !== 'editor') return;
    intent.current = null;
    form.current?.querySelector<HTMLElement>('input, textarea, select')?.focus();
  }, [intent]);
  const outgoing = link.sourceId === docId;
  const otherId = outgoing ? link.targetId : link.sourceId;
  const otherTitle = titleOf(otherId);
  const home = homeControl(link, type, docId, otherTitle);

  const refresh = () =>
    Promise.all([
      client.invalidateQueries({ queryKey: ['links'] }),
      client.invalidateQueries({ queryKey: ['tree'] }),
      client.invalidateQueries({ queryKey: ['documents', 'meta'] }),
    ]);

  const write = async (run: () => Promise<unknown>): Promise<boolean> => {
    try {
      await run();
      await refresh();
      return true;
    } catch (error) {
      if (error instanceof ApiError && error.status === 400) setRefused(touched());
      else reportFailure(notify, error);
      if (error instanceof ApiError && error.code === 'row_conflict') await refresh();
      return false;
    }
  };

  const touched = (): string[] => {
    const initial = metaInputs(fields ?? [], link.meta);
    return (fields ?? []).filter((f) => inputs[f.key] !== initial[f.key]).map((f) => f.key);
  };

  const save = () => {
    const patch = changedPatch(link, typed, fields, inputs);
    if (patch === null) return;
    setRefused(null);
    // The save remounts this editor (keyed on rv); focus returns to its form.
    intent.current = { kind: 'editor' };
    void write(() => api.update(link.sourceId, link.id, { baseRv: link.rv, ...patch })).then(
      (done) => {
        if (!done && intent.current?.kind === 'editor') intent.current = null;
      },
    );
  };

  return (
    <div className="cx-links-editor">
      <Button
        size="sm"
        variant="ghost"
        onClick={() => {
          intent.current = { kind: 'row', id: link.id };
          selectLink(docId, null);
        }}
      >
        Back to all links
      </Button>
      <dl className="cx-links-editor__facts">
        <dt>Type</dt>
        <dd>{type?.label ?? ''}</dd>
        <dt>From</dt>
        <dd>
          <PageLink id={link.sourceId} docId={docId} title={titleOf(link.sourceId)} open={open} />
          {isReference(link.sourceId) && <SrdBadge />}
        </dd>
        <dt>To</dt>
        <dd>
          <PageLink id={link.targetId} docId={docId} title={titleOf(link.targetId)} open={open} />
          {isReference(link.targetId) && <SrdBadge />}
        </dd>
      </dl>
      {home && (
        <Button
          size="sm"
          onClick={() =>
            void write(() =>
              api.update(link.sourceId, link.id, { baseRv: link.rv, isPrimary: home.isPrimary }),
            )
          }
        >
          {home.label}
        </Button>
      )}
      <form
        ref={form}
        noValidate
        aria-label="Link details"
        className="cx-links-note-form"
        onSubmit={(event) => {
          event.preventDefault();
          save();
        }}
      >
        <TextField
          label="Beat"
          maxLength={500}
          value={typed.label}
          onChange={(e) => setTyped({ ...typed, label: e.target.value })}
        />
        <TextField
          label="Role"
          maxLength={500}
          value={typed.role}
          onChange={(e) => setTyped({ ...typed, role: e.target.value })}
        />
        <div className="cx-field">
          <label htmlFor={noteId} className="cx-field__label">
            Note
          </label>
          <textarea
            id={noteId}
            className="cx-links-textarea"
            maxLength={10_000}
            rows={3}
            value={typed.note}
            onChange={(e) => setTyped({ ...typed, note: e.target.value })}
          />
        </div>
        {fields === null && Object.keys(link.meta).length > 0 && (
          <>
            <p className="cx-field__label">Meta (read-only)</p>
            <pre className="cx-links-editor__meta">{JSON.stringify(link.meta, null, 2)}</pre>
          </>
        )}
        {(fields ?? []).map((field) => (
          <MetaInputField
            key={field.key}
            field={field}
            value={inputs[field.key] ?? ''}
            error={refused?.includes(field.key) ? VALUE_REFUSED_TEXT : undefined}
            onChange={(value) => setInputs({ ...inputs, [field.key]: value })}
          />
        ))}
        {refused !== null && refused.length === 0 && <p role="alert">{VALUE_REFUSED_TEXT}</p>}
        <div className="cx-links-note-form__actions">
          <Button variant="primary" size="sm" type="submit">
            Save
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() =>
              void (async () => {
                // The intent is set first: the refresh inside the write unmounts this editor.
                intent.current = { kind: 'heading' };
                const done = await write(() => api.remove(link.sourceId, link.id, link.rv));
                if (done) notify(removedNotice(otherTitle));
                else if (intent.current?.kind === 'heading') intent.current = null;
              })()
            }
          >
            Remove link
          </Button>
        </div>
      </form>
    </div>
  );
}

function PageLink({
  id,
  docId,
  title,
  open,
}: {
  id: string;
  docId: string;
  title: string;
  open: ReturnType<typeof useOpenDoc>;
}) {
  if (id === docId) return <span>This page</span>;
  return (
    <a href={docHref(id)} onClick={(e) => open(id, e)}>
      {title}
    </a>
  );
}

function MetaInputField({
  field,
  value,
  error,
  onChange,
}: {
  field: MetaField;
  value: MetaInput;
  error: string | undefined;
  onChange: (value: MetaInput) => void;
}) {
  const label = labelOfKey(field.key);
  const id = useId();
  if (field.kind === 'boolean') {
    return <Checkbox label={label} checked={value === true} onChange={onChange} />;
  }
  if (field.kind === 'enum') {
    return (
      <div className="cx-field">
        <label htmlFor={id} className="cx-field__label">
          {label}
        </label>
        <select
          id={id}
          className="cx-links-select"
          value={String(value)}
          onChange={(e) => onChange(e.target.value)}
        >
          {!field.required && <option value="" />}
          {field.options?.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
        {error && <span role="alert">{error}</span>}
      </div>
    );
  }
  const numeric = field.kind === 'integer' || field.kind === 'number';
  return (
    <TextField
      label={label}
      description={error}
      aria-invalid={error ? true : undefined}
      value={String(value)}
      {...(numeric
        ? {
            type: 'number',
            step: field.kind === 'integer' ? 1 : 'any',
            ...(field.minimum === undefined ? {} : { min: field.minimum }),
            ...(field.maximum === undefined ? {} : { max: field.maximum }),
          }
        : {})}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}
